import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import ApiStatusOverview from '../components/ApiStatusOverview';
import CalendarStatus from '../components/CalendarStatus';
import UserManagement from '../components/UserManagement';
import GamesDataBrowser from '../components/admin/GamesDataBrowser';
import VenuesDataBrowser from '../components/admin/VenuesDataBrowser';
import RankingsDataBrowser from '../components/admin/RankingsDataBrowser';
import LinesDataBrowser from '../components/admin/LinesDataBrowser';
import WatchDataPanel from '../components/admin/WatchDataPanel';
import AdminLockManager from '../components/admin/AdminLockManager';
import AdminManualLockCreator from '../components/admin/AdminManualLockCreator';
import AdminSeasonOverride from '../components/admin/AdminSeasonOverride';
import LeagueManagementPanel from '../components/admin/LeagueManagementPanel';
import DiscordChannelManager from '../components/admin/DiscordChannelManager';
import DramaAnalysis from '../components/admin/DramaAnalysis';
import LLMAdminPanel from '../components/admin/LLMAdminPanel';
import PlayoffManagementPanel from '../components/admin/PlayoffManagementPanel';
import PaymentCollectionPanel from '../components/admin/PaymentCollectionPanel';
import SMSAdminPanel from '../components/admin/SMSAdminPanel';
import ReenrollmentPanel from '../components/admin/ReenrollmentPanel';

import styles from '../styles/AdminDashboard.module.css';
import VersionFooter from '../components/VersionFooter';

const ADMIN_TABS = [
  { id: 'users', label: 'User Management' },
  { id: 'apiStatus', label: 'API Status Overview' },
  { id: 'locks', label: 'Locks' },
  { id: 'createLock', label: 'Create Manual Lock' },
  { id: 'games', label: 'Games Data' },
  { id: 'venues', label: 'Venues Data' },
  { id: 'lines', label: 'Lines Data' },
  { id: 'rankings', label: 'Rankings Data' },
  { id: 'calendar', label: 'Calendar Status' },
  { id: 'watchData', label: 'Watch Data' },
  { id: 'seasonOverride', label: 'Season Management' },
  { id: 'leagueManagement', label: 'League Management' },
  { id: 'discord', label: 'Discord Channels' },
  { id: 'drama', label: 'Drama Analysis' },
  { id: 'llm', label: 'LLM Admin' },
  { id: 'playoff', label: 'League Playoff' },
  { id: 'paymentCollection', label: 'Payment Collection' },
  { id: 'reenrollment', label: 'Season Re-enrollment' },
  { id: 'sms', label: 'SMS Admin' }
];

function AdminDashboard() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('users');
  const [error] = useState('');

  if (!user || user.role !== 'admin') {
    return <div className={styles.errorMessage}>You do not have permission to access this page.</div>;
  }

  const renderComponent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;
      case 'apiStatus':
        return <ApiStatusOverview />;
      case 'locks':
        return <AdminLockManager />;
      case 'createLock':
        return <AdminManualLockCreator />;
      case 'games':
        return <GamesDataBrowser />;
      case 'venues':
        return <VenuesDataBrowser />;
      case 'lines':
        return <LinesDataBrowser />;
      case 'rankings':
        return <RankingsDataBrowser />;
      case 'calendar':
        return <CalendarStatus />;
      case 'watchData':
        return <WatchDataPanel />;
      case 'seasonOverride':
        return <AdminSeasonOverride />;
      case 'leagueManagement':
        return <LeagueManagementPanel />;
      case 'discord':
        return <DiscordChannelManager />;
      case 'drama':
        return <DramaAnalysis />;
      case 'llm':
        return <LLMAdminPanel />;
      case 'playoff':
        return <PlayoffManagementPanel />;
      case 'paymentCollection':
        return <PaymentCollectionPanel />;
      case 'reenrollment':
        return <ReenrollmentPanel />;
      case 'sms':
        return <SMSAdminPanel />;
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.sidebar}>
        <h2 className={styles.dashboardTitle}>Admin Dashboard</h2>
        <label className={styles.mobileNavLabel} htmlFor="admin-section-select">
          Section
        </label>
        <select
          id="admin-section-select"
          className={styles.mobileNav}
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
        >
          {ADMIN_TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>{tab.label}</option>
          ))}
        </select>
        {ADMIN_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${styles.navButton} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.content}>
        {error && <p className={styles.errorMessage}>{error}</p>}
        {renderComponent()}
        <VersionFooter />
      </div>
    </div>
  );
}

export default AdminDashboard;
